import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { BookingCheck, StaffResolution } from './booking-checks-contracts'
import type { BookingChecksRepository } from './booking-checks-repository'

// Checks change on the watch's cadence (15 minutes or longer).
const REFRESH_INTERVAL_MS = 120_000

export function bookingChecksQueryKey(tenantId: string) {
  return ['booking-checks', tenantId] as const
}

/** The clinic's Booking checks; nothing is requested without a repository. */
export function useBookingChecks(
  repository: BookingChecksRepository | null | undefined,
  tenantId: string
) {
  return useQuery({
    queryKey: bookingChecksQueryKey(tenantId),
    queryFn: () => repository!.list(tenantId),
    enabled: Boolean(repository && tenantId),
    refetchInterval: REFRESH_INTERVAL_MS,
  })
}

/** Staff record what they did; the list refreshes from the server after. */
export function useResolveBookingCheck(
  repository: BookingChecksRepository | null | undefined,
  tenantId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      check,
      resolutionType,
    }: {
      check: BookingCheck
      resolutionType: StaffResolution
    }) => repository!.resolve(tenantId, check, resolutionType),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: bookingChecksQueryKey(tenantId),
      }),
  })
}

/** The check staff should see for one appointment: an open one first. */
export function bookingCheckForAppointment(
  checks: BookingCheck[] | undefined,
  appointmentId: string
): BookingCheck | null {
  const matching = (checks ?? []).filter(
    (check) => check.appointmentId === appointmentId
  )
  return (
    matching.find((check) => check.status === 'OPEN') ?? matching[0] ?? null
  )
}
